import { useRef, useState } from 'react'
import { FocusTrapDialog } from './FocusTrapDialog'
import { InlineAlert } from './InlineAlert'

export interface MandatoryCommentModalProps {
  /** e.g. "Adjust qualifying %" */
  title: string
  /** e.g. "Qualifying %" — the field being adjusted. */
  fieldLabel: string
  /** The current value before this edit, or `null` for "Not yet assessed". */
  beforeValue: number | null
  min: number
  max: number
  submitting: boolean
  /** Verbatim server violation message (e.g. `REASON_TOO_SHORT`) — surfaced in-modal, not a toast. */
  serverError: string | null
  onClose: () => void
  onSubmit: (afterValue: number, comment: string) => void
}

/**
 * THE one guarded-write UI pattern (CLAUDE.md §2.1, docs/design/IB-53-film-ui-design.md §3.2).
 * Every guarded field in the product (film `qualifyingPct` today; R&D `qre_pct` when that screen
 * ships) opens this same modal — never a per-screen re-implementation. Responsibilities kept
 * deliberately narrow: client-side clamp (0–100 by default, immediate feedback) + a required
 * comment, both defense-in-depth only. The actual write is the caller's job — this component never
 * calls `fetch`/`apiClient` itself, so it cannot itself grow a second write path.
 */
export function MandatoryCommentModal({
  title,
  fieldLabel,
  beforeValue,
  min,
  max,
  submitting,
  serverError,
  onClose,
  onSubmit,
}: MandatoryCommentModalProps) {
  const [afterInput, setAfterInput] = useState(beforeValue !== null ? String(beforeValue) : '')
  const [comment, setComment] = useState('')
  const afterRef = useRef<HTMLInputElement>(null)

  const parsedAfter = afterInput.trim() === '' ? null : Number(afterInput)
  const afterInRange = parsedAfter !== null && Number.isFinite(parsedAfter) && parsedAfter >= min && parsedAfter <= max
  const commentValid = comment.trim().length > 0
  const canSubmit = afterInRange && commentValid && !submitting

  function clamp(value: number): number {
    return Math.min(max, Math.max(min, value))
  }

  function handleAfterBlur() {
    if (parsedAfter === null || !Number.isFinite(parsedAfter)) return
    const clamped = clamp(parsedAfter)
    if (clamped !== parsedAfter) setAfterInput(String(clamped))
  }

  function handleSubmit() {
    if (!canSubmit || parsedAfter === null) return
    onSubmit(clamp(parsedAfter), comment.trim())
  }

  return (
    <FocusTrapDialog
      titleId="mandatory-comment-modal-title"
      title={title}
      onClose={onClose}
      initialFocusRef={afterRef}
      footer={
        <>
          <button type="button" className="iw-btn" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button type="button" className="iw-btn iw-btn--primary" onClick={handleSubmit} disabled={!canSubmit}>
            {submitting ? 'Saving…' : 'Save adjustment'}
          </button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault()
          handleSubmit()
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-4)' }}
      >
        {serverError ? <InlineAlert severity="error">{serverError}</InlineAlert> : null}

        <div className="iw-field">
          <span className="iw-field-label">Before</span>
          <div style={{ color: 'var(--iw-text-muted)' }}>
            {beforeValue !== null ? `${beforeValue}%` : 'Not yet assessed'}
          </div>
        </div>

        <div className="iw-field">
          <label className="iw-field-label" htmlFor="mandatory-comment-after">
            {fieldLabel} (after)
          </label>
          <input
            id="mandatory-comment-after"
            ref={afterRef}
            className="iw-input"
            type="number"
            min={min}
            max={max}
            step="0.01"
            value={afterInput}
            onChange={(e) => setAfterInput(e.target.value)}
            onBlur={handleAfterBlur}
            required
          />
          {parsedAfter !== null && !afterInRange ? (
            <span style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-danger)' }}>
              Must be between {min} and {max}.
            </span>
          ) : null}
        </div>

        <div className="iw-field">
          <label className="iw-field-label" htmlFor="mandatory-comment-reason">
            Reason (required)
          </label>
          <textarea
            id="mandatory-comment-reason"
            className="iw-input"
            rows={3}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            required
            placeholder="Why is this adjustment being made?"
          />
        </div>
      </form>
    </FocusTrapDialog>
  )
}
